// controls/file.js — champ d’upload de fichier(s)
import React, { useState, useEffect, useRef } from "react";
import CommonControls from "./inc/Common";
import { HiExclamationCircle } from "react-icons/hi";
import { HiOutlineEye, HiOutlineDocument, HiOutlinePlus, HiOutlineCloudArrowUp, HiOutlineTrash } from "react-icons/hi2";
import { BsCheckCircle } from "react-icons/bs";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "api";
import { debug } from "utils";
import {
  useFormBuilderRenderContext
} from "../../FormBuilderRenderContext";
import './file.css';
/**
 * Création d’un champ fichier conforme JSON Schema draft 2020-12
 */
export function createFileField() {
  return {
    type: "string",
    title: "Fichier",
    "x-ui": "file",
    "x-accept": "",
    "x-required": true,
    "x-multiple": true,
    "x-maxSize": "", // en Mo (indicatif)
  };
}
/**
 * 🧩 Éditeur du champ Fichier (FieldEditor)
 */
export function EditFileField({ field, onChange }) {
  const presetToAccept = {
    pdf: ".pdf", // documents sûrs
    images: ".jpg,.jpeg,.png,.gif,.bmp,.webp,.tif,.tiff",
    audio: ".mp3,.wav,.ogg",
    video: ".mp4,.webm",
    structured: ".json,.xml,.csv,.txt",
    archives: ".zip", // à autoriser uniquement si besoin d’import ZIP
    office: ".doc,.docx,.xls,.xlsx,.odt,.ods", // ⚠️ à éviter par défaut
  };
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [accept, setAccept] = useState(field["x-accept"] || "");
  const [multiple, setMultiple] = useState(field["x-multiple"] || false);
  const [maxSize, setMaxSize] = useState(field["x-maxSize"] || "");
  // 🔄 Synchronisation vers le parent
  useEffect(() => onChange("x-accept", accept), [accept]);
  useEffect(() => onChange("x-multiple", multiple), [multiple]);
  useEffect(() => onChange("x-maxSize", maxSize), [maxSize]);
  const handlePresetChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setSelectedPresets(selected);
    const combined = selected
      .map((key) => presetToAccept[key])
      .filter(Boolean)
      .join(",");
    setAccept(combined);
  };
  return (
    <>
      <CommonControls field={field} onChange={onChange} />
      {/* ---- Types MIME (presets multiples) ---- */}
      <div className="mb-3">
        <label className="form-label">Types de fichiers autorisés</label>
        <select
          multiple
          className="form-select form-select-sm"
          value={selectedPresets}
          onChange={handlePresetChange}
        >
          <option value="pdf">Documents PDF</option>
          <option value="images">Images (JPG, PNG, GIF…)</option>
          <option value="audio">Audio (MP3, WAV…)</option>
          <option value="video">Vidéo (MP4, WEBM…)</option>
          <option value="structured">JSON / XML</option>
          <option value="archives">Archives ZIP</option>
          <option value="documents">Autres documents (Word, Excel…)</option>
        </select>
        <small className="text-muted d-block mt-1">
          Maintenez <kbd>Ctrl</kbd> ou <kbd>Cmd</kbd> pour sélectionner
          plusieurs catégories.
        </small>
      </div>
      {/* ---- Extensions personnalisées ---- */}
      <div className="mb-3">
        <label className="form-label">Extensions personnalisées</label>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder=".pdf,.png,.jpg"
          value={accept}
          onChange={(e) => setAccept(e.target.value)}
        />
        <small className="text-muted">
          Liste séparée par des virgules — ex : <code>.pdf,.jpg,.png</code>
        </small>
      </div>
      {/* ---- Taille maximale ---- */}
      <div className="mb-3">
        <label className="form-label">Taille maximale (Mo)</label>
        <input
          type="number"
          className="form-control form-control-sm"
          placeholder="ex : 5"
          value={maxSize}
          onChange={(e) => setMaxSize(e.target.value)}
        />
        <small className="text-muted">
          Indique la taille maximale autorisée (information seulement).
        </small>
      </div>
      {/* ---- Mode multiple ---- */}
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id={`multiple_${field.id}`}
          checked={multiple}
          onChange={(e) => setMultiple(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={`multiple_${field.id}`}>
          Autoriser la sélection de plusieurs fichiers
        </label>
      </div>
    </>
  );
}
export const RenderFileField = ({
  field,
  value,
  onChange,
  className,
  isReadOnly,
  errors,
  analysis = {}   // <--- AJOUT
}) => {
  const { fbOpenDocument, fbDeleteDocument, fbIsAdmin } = useFormBuilderRenderContext();
  const accept = field["x-accept"] || "";
  const multiple = field["x-multiple"] || false;
  const maxSize = field["x-maxSize"] || "";
  const isRequired =
    field["x-effective-required"] === true ||
    field["x-required"] === true;
  const fileInputRef = useRef(null);
  /// const filesBuffer = useRef(Array.isArray(value) ? [...value] : []);
  const [uploadState, setUploadState] = useState("empty");
  const [uploadProgress, setUploadProgress] = useState(0);
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      // filesBuffer.current = [...value];
      setUploadState("done");
    } else {
      setUploadState("empty");
    }
  }, [value]);
  const triggerSelect = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const uploadFile = async (file) => {
    if (isReadOnly) return;

    setUploadState("uploading");
    setUploadProgress(0);

    const form = new FormData();
    form.append("file", file);
    form.append("accept", accept || "");
    form.append("maxSize", maxSize || "");

    try {
      const res = await api.post("/formbuilder/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total > 0) {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(percent);
          }
        }
      });

      const data = res.data;

      const fileObj = {
        path: data.path,
        name: data.name,
        size: data.size,
        ext: data.ext,
        mime: data.mime,
        uploadedAt: Math.floor(Date.now() / 1000),
      };

      // 🔥 source unique = value
      const currentFiles = Array.isArray(value) ? value : [];

      const newFiles = multiple
        ? [...currentFiles, fileObj]
        : [fileObj];

      onChange?.(newFiles);

      setUploadState("done");
      setUploadProgress(100);

    } catch (error) {
      console.error("UPLOAD ERROR", error);

      setUploadState("empty");
      setUploadProgress(0);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || isReadOnly) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const arr = [...value];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);

    onChange([...arr]);
  };
  const handleChange = async (e) => {
    if (isReadOnly) return;

    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadState("uploading");
    setUploadProgress(0);

    try {
      const uploadedFiles = [];

      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("accept", accept || "");
        form.append("maxSize", maxSize || "");

        const res = await api.post("/formbuilder/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const data = res.data;

        uploadedFiles.push({
          path: data.path,
          name: data.name,
          size: data.size,
          ext: data.ext,
          mime: data.mime,
          uploadedAt: Math.floor(Date.now() / 1000),
        });
      }

      const currentFiles = Array.isArray(value) ? value : [];

      const newFiles = multiple
        ? [...currentFiles, ...uploadedFiles]
        : [uploadedFiles[0]];

      onChange?.(newFiles);

      setUploadState("done");
      setUploadProgress(100);

    } catch (error) {
      console.error("UPLOAD ERROR", error);

      setUploadState("empty");
      setUploadProgress(0);
    }
  };

  const deleteFile = (i) => {
    if (isReadOnly) {
      return;
    }

    const currentFiles = Array.isArray(value) ? value : [];

    const newFiles = currentFiles.filter((_, index) => index !== i);

    onChange(newFiles);
  };
  const isEmpty = uploadState === "empty";
  const isUploading = uploadState === "uploading";
  const isDone = uploadState === "done";
  return (

    <div className={`fb-field fb-file ${field["x-bloc-class"] || ""} ${field["x-class"] || ""} ${className || ""} ${isRequired ? "fb-required" : ""}`}>

      {/* INPUT invisible */}
      <input
        type="file"
        ref={fileInputRef}
        className="d-none"
        accept={accept}
        multiple={multiple}
        readOnly={isReadOnly}
        onChange={handleChange}

      />
      {/* MINI-CARD */}
      <div className={`fb-file-card ${errors ? "fb-file-card-error" : ""}`}>
        <div className={`fb-file-title`}>
          {field.title}
        </div>
        <div
          className={
            `fb-file-body d-flex flex-column ` +
            (isDone
              ? "align-items-stretch justify-content-start"
              : "align-items-center justify-content-center")
          }
        >
          {isDone && Array.isArray(value) && (
            <div className="">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="files">
                  {(provided) => (
                    <div
                      className="fb-file-list"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {value.map((f, i) => {
                        const fileAnalysis = analysis?.files?.[f.path] || null;
                        return (
                          <Draggable key={i} draggableId={String(i)} index={i}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="fb-file-item"
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.6 : 1,
                                  cursor: "grab"
                                }}
                              >
                                <span className="fb-file-index">{i + 1}.</span>

                                <span className="fb-file-name" onClick={() => { fbOpenDocument(f, field) }}>
                                  {f.name}
                                </span>
                                {fbIsAdmin}

                                {fileAnalysis?.status === "rejected" && (
                                  <div className="text-danger d-flex align-items-center gap-1">
                                    <HiExclamationCircle />
                                    {fileAnalysis.comment}
                                  </div>
                                )}

                                {fileAnalysis?.status === "accepted" && (
                                  <div className="text-success d-flex align-items-center gap-1">
                                    <BsCheckCircle />
                                    <span className="small">Validé</span>
                                  </div>
                                )}
                                {(!isReadOnly && (!fileAnalysis?.status || fbIsAdmin)) && <div className="text-success d-flex align-items-center gap-1">
                                  <HiOutlineTrash
                                    role="button"
                                    className="text-danger"
                                    onClick={() => { fbDeleteDocument(f, field) }} />
                                </div>
                                }
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
          <div className="text-center">
            {(!isUploading && !isReadOnly) && (
              <div onClick={triggerSelect}>
                <HiOutlineCloudArrowUp size={40} className="text-secondary " />
                <div className="text-muted small"></div>
              </div>
            )}
            {isUploading && (
              <div className="text-muted small">{uploadProgress}%</div>
            )}
          </div>
        </div>
      </div>
      {/* MESSAGE D’ERREUR */}
      {/* errors && (
        <div className="invalid-feedback d-block mt-1">
          {errors}
        </div>
      ) */}
    </div>
  );
};
